/**
 * Shop Icons Slider - JavaScript
 * Handles Flickity initialization and mobile layout switching
 */

class ShopIconsSlider {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.carousel = section.querySelector('[data-section-carousel]');
    this.prevButton = section.querySelector('[data-slider-prev]');
    this.nextButton = section.querySelector('[data-slider-next]');
    this.mobileLayout = section.dataset.mobileLayout;
    this.flickityInstance = null;
    this.isInitialized = false;

    this.init();
    this.bindEvents();
  }

  init() {
    // Check if we should initialize Flickity based on screen size and settings
    this.handleResponsive();
  }

 initFlickity() {
  if (!this.carousel || this.isInitialized) return;

  // Check if Flickity is available
  if (typeof Flickity === 'undefined') {
    console.error('Flickity is not loaded. Please ensure Flickity library is included.');
    return;
  }

  try {
    const isMobile = window.innerWidth < 768;
    
    this.flickityInstance = new Flickity(this.carousel, {
      cellAlign: 'left',
      contain: isMobile ? false : true, // Don't contain on mobile to show peek
      pageDots: false,
      prevNextButtons: false,
      wrapAround: true,
      groupCells: false,
      draggable: true,
      freeScroll: false,
      friction: 0.8,
      selectedAttraction: 0.2,
      accessibility: true,
      setGallerySize: true,
      resize: true,
      // Show percentage of next slide on mobile
      watchCSS: false
    });

    this.isInitialized = true;
    this.updateNavigationState();

    // Listen to Flickity events
    this.flickityInstance.on('change', () => {
      this.updateNavigationState();
    });

  } catch (error) {
    console.error('Error initializing Flickity:', error);
  }
}

  destroyFlickity() {
    if (this.flickityInstance && this.isInitialized) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
      this.isInitialized = false;
    }
  }

  handleResponsive() {
    const isMobile = window.innerWidth < 768;

    if (isMobile && this.mobileLayout === 'stack') {
      // Destroy Flickity on mobile stack layout
      this.destroyFlickity();
    } else {
      // Initialize Flickity for slider layout
      if (!this.isInitialized) {
        this.initFlickity();
      }
    }
  }

  updateNavigationState() {
    if (!this.flickityInstance || !this.prevButton || !this.nextButton) return;

    const selectedIndex = this.flickityInstance.selectedIndex;
    const slidesCount = this.flickityInstance.slides.length;

    // Since wrapAround is true, we don't need to disable buttons
    // But we can add visual feedback
    this.prevButton.disabled = false;
    this.nextButton.disabled = false;
  }

  bindEvents() {
    // Previous button click
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        if (this.flickityInstance) {
          this.flickityInstance.previous();
        }
      });
    }

    // Next button click
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        if (this.flickityInstance) {
          this.flickityInstance.next();
        }
      });
    }

    // Handle window resize with debounce
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResponsive();
      }, 250);
    });

    // Keyboard navigation
    if (this.carousel) {
      this.carousel.addEventListener('keydown', (e) => {
        if (!this.flickityInstance) return;

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.flickityInstance.previous();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.flickityInstance.next();
        }
      });
    }
  }

  // Public method to refresh the slider (useful for theme editor)
  refresh() {
    if (this.flickityInstance) {
      this.flickityInstance.resize();
      this.flickityInstance.reloadCells();
    }
  }

  // Public method to destroy instance (cleanup)
  destroy() {
    this.destroyFlickity();
  }
}

// Initialize all shop icons sliders on page
function initShopIconsSliders() {
  const sections = document.querySelectorAll('.shop-icons-slider');
  const instances = [];

  sections.forEach(section => {
    const instance = new ShopIconsSlider(section);
    instances.push(instance);
  });

  return instances;
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShopIconsSliders);
} else {
  initShopIconsSliders();
}

// Shopify Theme Editor Support
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('.shop-icons-slider');
    if (section) {
      new ShopIconsSlider(section);
    }
  });

  document.addEventListener('shopify:section:reorder', function(event) {
    const section = event.target.querySelector('.shop-icons-slider');
    if (section) {
      const instance = new ShopIconsSlider(section);
      instance.refresh();
    }
  });

  document.addEventListener('shopify:block:select', function(event) {
    const section = event.target.closest('.shop-icons-slider');
    if (section) {
      const carousel = section.querySelector('[data-section-carousel]');
      if (carousel && carousel.flickity) {
        const blockIndex = Array.from(event.target.parentElement.children).indexOf(event.target);
        carousel.flickity.select(blockIndex);
      }
    }
  });
}

// Export for module usage (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShopIconsSlider;
}
